<template>
    <div class="vuetify-wrapper">
        <div class="v-application v-application--is-ltr">
            <v-card outlined elevation="0" class="rounded-lg v-application">
                <v-card-text>
                    <v-row dense align="center">
                        <v-col cols="12" md="3">
                            <v-menu
                                v-model="isDateMenuOpen"
                                :close-on-content-click="false"
                                transition="scale-transition"
                                offset-y
                                min-width="auto"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        :value="dateLabel"
                                        v-on="on"
                                        v-bind="attrs"
                                        label="Datum"
                                        prepend-inner-icon="mdi-calendar"
                                        readonly
                                        clearable
                                        hide-details
                                        placeholder="Alle"
                                        @click:clear="clearDates"
                                    />
                                </template>
                                <v-date-picker
                                    v-model="selectedDates"
                                    multiple
                                    no-title
                                    locale="de-DE"
                                    :first-day-of-week="1"
                                >
                                    <v-spacer />
                                    <v-btn text @click="selectedDates = []">Zurücksetzen</v-btn>
                                    <v-btn text color="primary" @click="isDateMenuOpen = false">Übernehmen</v-btn>
                                </v-date-picker>
                            </v-menu>
                        </v-col>

                        <v-col cols="12" md="3">
                            <v-text-field
                                v-model="pharmacy"
                                label="Apotheke"
                                prepend-inner-icon="mdi-magnify"
                                clearable
                                hide-details
                            />
                        </v-col>

                        <v-col cols="12" md="3">
                            <v-select
                                v-model="wfStateFilter"
                                :items="wfStateFilterOptions"
                                class="wf-state-filter"
                                label="Zustand"
                                clearable
                                hide-details
                            />
                        </v-col>

                        <v-col cols="12" md="3">
                            <v-select
                                v-model="requestStatusFilter"
                                :items="requestStatusFilterOptions"
                                label="Anfragestatus"
                                clearable
                                hide-details
                            />
                        </v-col>
                    </v-row>
                </v-card-text>

                <v-data-table
                    :headers="headers"
                    :items="items"
                    :server-items-length="total"
                    :loading="isLoading"
                    loading-text="Wird geladen..."
                    no-data-text="Keine angefragten Termine"
                    :options.sync="tableOptions"
                    @update:options="onOptionsChange"
                    disable-filtering
                    class="elevation-0 v-application--is-ltr"
                    :footer-props="{
                        'items-per-page-options': [10, 25, 50],
                        'show-first-last-page': true,
                        'items-per-page-text': 'Zeilen pro Seite'
                    }"
                >
                    <template v-slot:item="{ item }">
                        <tr>
                            <td>{{ formatDateRange(item.appointments) }}</td>
                            <td>
                                <!--
                                    Only a pharmacy that still exists gets a
                                    link. A removed one keeps its row through
                                    pharmacy_snapshot, but /pharmacies/<id>
                                    would 404, so the name renders as text.
                                -->
                                <div>
                                    <a
                                        v-if="item.pharmacy.exists"
                                        :href="`/pharmacies/${item.pharmacy._id}`"
                                        target="_blank"
                                    >
                                        {{ item.pharmacy.name }}
                                    </a>
                                    <span v-else :title="'Diese Apotheke wurde gelöscht.'">
                                        {{ item.pharmacy.name }}
                                    </span>
                                </div>
                                <div class="text-caption grey--text">
                                    {{ item.pharmacy.customer_id }} &middot; {{ item.pharmacy.zip }} {{ item.pharmacy.city }}
                                </div>
                            </td>
                            <td>
                                <div v-for="apt in item.appointments" :key="apt.date">
                                    <template v-if="item.appointments.length > 1">{{ formatDayMonth(apt.date) }} - </template>{{ apt.time_from }}-{{ apt.time_to }}
                                </div>
                            </td>
                            <td>{{ item.assigned_pharmacist || '-' }}</td>
                            <td>{{ wfStateLabel(item.wf_state) }}</td>
                            <td>{{ requestStatusLabel(item.request_status) }}</td>
                            <td>{{ formatDate(item.request_date) }}</td>
                        </tr>
                    </template>
                </v-data-table>
            </v-card>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import _ from 'lodash';
import { mapMutations } from 'vuex';
import { filters as appointmentRequestsFilters } from '../../constants';

const REQUEST_STATUS_OPTIONS = [
    { text: 'Offen', value: 'NotSet' },
    { text: 'Zugesagt', value: 'Accepted' },
    { text: 'Abgesagt', value: 'Rejected' },
    { text: 'Automatisch abgelehnt (andere FB Zuweisung)', value: 'Auto_Rejected_Assigned' },
    { text: 'Automatisch abgelehnt (Löschung)', value: 'Auto_Rejected_Delete' },
    { text: 'Absage zurückgezogen', value: 'CancellationWithdrawn' },
    { text: 'Erneut angefragt', value: 'ReRequested' },
];

export default {
    name: 'PharmacistRequestedAppointmentsTable',

    props: {
        pharmacist_id: String,
    },

    data() {
        return {
            items: [],
            total: 0,
            isLoading: false,
            pharmacy: '',
            selectedDates: [],
            isDateMenuOpen: false,
            wfStateFilter: null,
            requestStatusFilter: null,
            wfStateFilterOptions: appointmentRequestsFilters.appointmentGroupStatusOptions,
            requestStatusFilterOptions: REQUEST_STATUS_OPTIONS,
            tableOptions: {},
            // Monotonic counter identifying the in-flight fetch; see fetchItems.
            requestToken: 0,
            headers: [
                { text: 'Datum', value: 'date' },
                { text: 'Apotheke', value: 'pharmacy' },
                { text: 'Zeit', value: 'time' },
                { text: 'Belegung', value: 'assigned_pharmacist' },
                { text: 'Zustand', value: 'wf_state', sortable: false },
                { text: 'Anfragestatus', value: 'request_status', sortable: false },
                { text: 'Datum Anfrage', value: 'request_date' },
            ],
        };
    },

    computed: {
        dateLabel() {
            return this.selectedDates
                .slice()
                .sort()
                .map(date => this.formatDate(date))
                .join(', ');
        },
    },

    created() {
        this.debouncedFilterChanged = _.debounce(this.filterChanged, 400);
    },

    watch: {
        pharmacy() {
            this.debouncedFilterChanged();
        },
        isDateMenuOpen(isOpen) {
            if (!isOpen) this.filterChanged();
        },
        wfStateFilter() {
            this.filterChanged();
        },
        requestStatusFilter() {
            this.filterChanged();
        },
    },

    methods: {
        formatDate(date) {
            if (!date) return '-';
            return new Date(date).toLocaleDateString('de-DE', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
            });
        },
        formatDayMonth(date) {
            if (!date) return '-';
            return new Date(date).toLocaleDateString('de-DE', {
                day: '2-digit',
                month: '2-digit',
            });
        },
        wfStateLabel(value) {
            return this.wfStateFilterOptions.find(option => option.value === value)?.text || value || '-';
        },
        requestStatusLabel(value) {
            return this.requestStatusFilterOptions.find(option => option.value === value)?.text || value || '-';
        },
        formatDateRange(appointments) {
            if (!appointments || !appointments.length) return '-';
            const first = appointments[0].date;
            const last = appointments[appointments.length - 1].date;
            if (first === last) return this.formatDate(first);
            return `${this.formatDate(first)} - ${this.formatDate(last)}`;
        },
        filterChanged() {
            this.tableOptions.page = 1;
            this.fetchItems();
        },
        clearDates() {
            this.selectedDates = [];
            this.filterChanged();
        },
        onOptionsChange() {
            this.fetchItems();
        },
        async fetchItems() {
            if (!this.pharmacist_id) return;
            const { page = 1, itemsPerPage = 10, sortBy = [], sortDesc = [] } = this.tableOptions;

            // Every filter and table-options change starts its own request, and
            // they can finish out of order. Only the newest may touch the table:
            // otherwise a slow earlier response repaints rows that contradict
            // the filters currently on screen, and the stale one clearing
            // isLoading would hide the spinner while a newer request runs.
            const token = ++this.requestToken;

            this.isLoading = true;
            try {
                const response = await axios.get(`/api/pharmacist/${this.pharmacist_id}/requested-appointments`, {
                    params: {
                        page,
                        page_size: itemsPerPage,
                        sort_by: sortBy[0] || 'date',
                        sort_asc: sortDesc[0] ? -1 : 1,
                        pharmacy: this.pharmacy || undefined,
                        dates: this.selectedDates.length ? this.selectedDates.join(',') : undefined,
                        wf_state: this.wfStateFilter || undefined,
                        request_status: this.requestStatusFilter || undefined,
                    },
                });
                if (token !== this.requestToken) return;
                this.items = response.data?.data || [];
                this.total = response.data?.total || 0;
            } catch (error) {
                if (token !== this.requestToken) return;
                this.alertError();
                this.items = [];
                this.total = 0;
            } finally {
                if (token === this.requestToken) {
                    this.isLoading = false;
                }
            }
        },
        ...mapMutations({
            alertError: 'alert/error',
        }),
    },
};
</script>

<style scoped>
.vuetify-wrapper .v-application {
    display: block;
}
</style>
